import { useLayoutEffect, useRef, useState } from "react"

import { cn } from "@/lib/utils"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"

interface OverflowTooltipProps {
  text: string
  className?: string
}

function OverflowTooltip({ text, className }: OverflowTooltipProps) {
  const textRef = useRef<HTMLSpanElement>(null)
  const [isOverflowing, setIsOverflowing] = useState(false)

  useLayoutEffect(() => {
    const element = textRef.current
    if (!element) return

    const measureOverflow = () => {
      setIsOverflowing(
        element.scrollWidth > element.clientWidth || element.scrollHeight > element.clientHeight
      )
    }

    measureOverflow()

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measureOverflow)
      return () => window.removeEventListener("resize", measureOverflow)
    }

    const observer = new ResizeObserver(measureOverflow)
    observer.observe(element)
    return () => observer.disconnect()
  }, [text])

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          ref={textRef}
          className={cn("block min-w-0", className)}
          tabIndex={isOverflowing ? 0 : undefined}
        >
          {text}
        </span>
      </TooltipTrigger>
      {isOverflowing ? <TooltipContent>{text}</TooltipContent> : null}
    </Tooltip>
  )
}

export { OverflowTooltip }
